import apiClient from '@/services/api';
import { useAuthStore } from '@/stores/auth';

const API_URL = '/units';

// Helper to ensure rates are numbers and prepare data for sending
const sanitizeUnitData = (unitData) => {
  const data = { ...unitData }; // Create a shallow copy to avoid mutating original
  const authStore = useAuthStore();
  data.company_id = data.company_id || authStore.currentUser?.company_id;

  // Ensure numeric fields are numbers
  ['monthly_rate','weekly_rate','daily_rate',
    'bedrooms','bathrooms','size_sqft'
  ].forEach(f => {
    if (data[f] !== null && data[f] !== undefined && data[f] !== '')
      data[f] = Number(data[f]);
  });

  // Normalise enums to backend's exact casing
  if (data.unit_type) data.unit_type = data.unit_type.toLowerCase();   // apartment | house …
  if (data.status)    data.status    = data.status.toLowerCase();      // available | occupied …

  return data;
};

export default {
  async getUnits() {
    const authStore = useAuthStore();
    const userCompanyId = authStore.currentUser?.company_id;
    
    // Build URL with company filter if user has a company_id
    let url = API_URL;
    if (userCompanyId) {
      const filter = {"field":"company_id","value":userCompanyId};
      url    = `${API_URL}?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    }
    
    const response = (await apiClient.get(url));

    // Backend now controls if images are included in list view (currently set to false)
    return response.data.map(unit => ({
        ...unit,
        // Ensure rates are numbers for frontend consistency if backend sends them as strings
        monthly_rate: parseFloat(unit.monthly_rate),
        weekly_rate: parseFloat(unit.weekly_rate),
        daily_rate: parseFloat(unit.daily_rate),
        bedrooms    : unit.bedrooms  != null ? +unit.bedrooms  : null,
        bathrooms   : unit.bathrooms != null ? +unit.bathrooms : null,
        size_sqft   : unit.size_sqft != null ? +unit.size_sqft : null,
    }));
  },

  async getUnit(id) {
    const response = (await apiClient.get(`${API_URL}/${id}`));
    // Single unit GET should include base64 images from backend
    return {
        ...response.data,
        monthly_rate: parseFloat(response.data.monthly_rate),
        weekly_rate: parseFloat(response.data.weekly_rate),
        daily_rate: parseFloat(response.data.daily_rate),
        bedrooms    : response.data.bedrooms  != null ? +response.data.bedrooms  : null,
        bathrooms   : response.data.bathrooms != null ? +response.data.bathrooms : null,
        size_sqft   : response.data.size_sqft != null ? +response.data.size_sqft : null,
    };
  },

  async createUnit(unitData) {
    const dataToSend = sanitizeUnitData(unitData);
    const response = (await apiClient.post(API_URL, dataToSend));
    return { // Ensure consistency with getUnit response
        ...response.data,
        monthly_rate: parseFloat(response.data.monthly_rate),
        weekly_rate: parseFloat(response.data.weekly_rate),
        daily_rate: parseFloat(response.data.daily_rate),
        bedrooms    : response.data.bedrooms  != null ? +response.data.bedrooms  : null,
        bathrooms   : response.data.bathrooms != null ? +response.data.bathrooms : null,
        size_sqft   : response.data.size_sqft != null ? +response.data.size_sqft : null,
    };
  },

  async updateUnit(id, unitData) {
    const dataToSend = sanitizeUnitData(unitData);
    // For PUT, only send image_X_base64 if it's explicitly changed or cleared.
    // If unitData.image_1_base64 is undefined (not touched by user), don't send it.
    // If it's null/empty string, it means user cleared it.
    // If it's a new base64 string, user uploaded a new image.
    const payload = { ...dataToSend };

    // Smart handling for image updates:
    // Only include image fields in payload if they are meant to be updated or cleared.
    // If a field like `image_1_base64_touched` exists and is false, we can skip sending.
    // For simplicity here, we assume if `image_X_base64` is in `dataToSend`, it's intentional.
    // A more robust solution might involve tracking "dirty" fields.

    if (!('image_1_base64' in dataToSend)) delete payload.image_1_base64;
    if (!('image_2_base64' in dataToSend)) delete payload.image_2_base64;

    const response = (await apiClient.put(`${API_URL}/${id}`, payload));
    return { // Ensure consistency
        ...response.data,
        monthly_rate: parseFloat(response.data.monthly_rate),
        weekly_rate: parseFloat(response.data.weekly_rate),
        daily_rate: parseFloat(response.data.daily_rate),
        bedrooms    : response.data.bedrooms  != null ? +response.data.bedrooms  : null,
        bathrooms   : response.data.bathrooms != null ? +response.data.bathrooms : null,
        size_sqft   : response.data.size_sqft != null ? +response.data.size_sqft : null,
    };
  },

  async deleteUnit(id) {
    return (await apiClient.delete(`${API_URL}/${id}`));
  }
};